import * as XLSX from 'xlsx';

const STATUS_LABEL = {
  'full-day': 'Full Day',
  mismatch: 'Mismatch',
  present: 'Present',
  'missing-punch': 'Missing Punch',
  'no-record': 'No Attendance Recorded',
  'half-day': 'Half Day',
  leave: 'Leave',
  'half-leave': 'Half Day Leave',
  holiday: 'Holiday',
  sunday: 'Sunday',
};

function pad2(n) {
  return String(n).padStart(2, '0');
}

function stamp(d = new Date()) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ` +
    `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
}

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/* Decimal hours */
const formatHours = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return '—';
  return `${Number(n.toFixed(2))} h`;
};

/* Minutes → decimal hours */
const formatLateHours = (minutes) => {
  const n = Number(minutes);
  if (!Number.isFinite(n) || n <= 0) return '—';
  return `${Number((n / 60).toFixed(2))} h`;
};

export function exportAttendanceDetailsToExcel({ rows, filters }) {
  const activeFilters = Object.entries(filters || {})
    .filter(([, v]) => v && v !== 'All')
    .map(([k, v]) => `${k}: ${v}`)
    .join('   |   ') || 'None';

  const headerRows = [
    ['ATTENDANCE DETAILS'],
    [`Filters: ${activeFilters}`],
    [`Generated: ${stamp()}`],
    [`Records: ${rows.length}`],
    [],
  ];

  /* ── Column definitions ──────────────────────────────────────── */
  const columns = [
    { key: 'Date', label: 'Date', width: 12 },
    { key: 'Day', label: 'Day', width: 10 },
    {
      key: 'Status',
      label: 'Status',
      width: 22,
      format: (r) => {
        // API-provided HalfDay takes priority
        if (r.HalfDay === 'M') return 'Half Day (Morning)';
        if (r.HalfDay === 'A') return 'Half Day (Afternoon)';

        return STATUS_LABEL[r.Status] || r.Status || '';
      },
    },
    { key: 'InTime', label: 'In Time', width: 10 },
    { key: 'OutTime', label: 'Out Time', width: 10 },
    { key: 'LateMinutes', label: 'Late Hours', width: 14, lateHours: true },
    { key: 'TotalHours', label: 'Total Hours', width: 14, time: true },
    { key: 'NormalHours', label: 'Normal Hours', width: 14, time: true },
    { key: 'OTHours', label: 'OT Hours', width: 12, time: true },
    { key: 'PreOT', label: 'Pre OT Hours', width: 14, time: true },
    { key: 'PostOT', label: 'Post OT Hours', width: 14, time: true },
    { key: 'HolidayOT', label: 'Holiday OT Hours', width: 16, time: true },
    { key: 'SundayOT', label: 'Sunday OT Hours', width: 16, time: true },
  ];

  const columnHeader = columns.map((c) => c.label);

  const bodyRows = rows.map((r) =>
    columns.map((c) => {
      if (c.format) return c.format(r);
      if (c.lateHours) return formatLateHours(r[c.key]);
      if (c.time) return formatHours(r[c.key]);

      const v = r[c.key];
      return v === null || v === undefined ? '' : v;
    })
  );

  /* ── Totals row ─────────────────────────────────────────────── */
  const totalsRow = columns.map((c) => {
    if (c.key === 'Date') {
      return `TOTAL — ${rows.length} record${rows.length === 1 ? '' : 's'}`;
    }

    if (c.lateHours) {
      const totalMinutes = rows.reduce((s, r) => s + num(r[c.key]), 0);
      return formatLateHours(totalMinutes);
    }

    if (c.time) {
      const totalHours = rows.reduce((s, r) => s + num(r[c.key]), 0);
      return formatHours(totalHours);
    }

    return '';
  });

  const aoa = [...headerRows, columnHeader, ...bodyRows, [], totalsRow];
  const ws = XLSX.utils.aoa_to_sheet(aoa);

  ws['!cols'] = columns.map((c) => ({ wch: c.width || 12 }));

  const lastCol = columns.length - 1;

  ws['!merges'] = headerRows.slice(0, 4).map((_, i) => ({
    s: { r: i, c: 0 },
    e: { r: i, c: lastCol },
  }));

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Attendance Details');

  const d = new Date();
  const filename =
    `Attendance_Details_${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}.xlsx`;

  XLSX.writeFile(wb, filename);

  return filename;
}